import { lazy, useEffect, useState } from 'react'
import { Routes, Route } from 'react-router-dom'
import { useAuthStore } from './auth/useAuthStore'
import { getSession } from './api/auth'
import { useSettingsStore } from './stores/useSettingsStore'
import { UnlockScreen } from './components/UnlockScreen'
import { Layout } from './components/layout/Layout'
// HomePage and InstrumentDetails stay eager — they're the trading hot path and
// must never hit a Suspense fallback mid-session. Everything else is cold
// (history/admin views) and loads on demand to keep the main chunk small.
import { HomePage } from './pages/HomePage'
import { InstrumentDetails } from './pages/InstrumentDetails'
// Eager (not lazy): rendered outside <Layout>, so it can't lean on Layout's
// Suspense boundary, and its heavy dep (TickerPriceChart) is already eager.
import { ChartPopoutPage } from './pages/ChartPopoutPage'
import { initCache } from './db/bootstrap'
import { useRecordPortfolioSnapshot } from './hooks/useRecordPortfolioSnapshot'
import { useMarketCalendar } from './hooks/useMarketCalendar'
import { toast } from './toast/useToastStore'

const PLCalendar = lazy(() =>
  import('./pages/PLCalendar').then((m) => ({ default: m.PLCalendar })),
)
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
)
const AccountsPage = lazy(() =>
  import('./pages/AccountsPage').then((m) => ({ default: m.AccountsPage })),
)
const TradesPage = lazy(() =>
  import('./pages/TradesPage').then((m) => ({ default: m.TradesPage })),
)
const TradeDetailPage = lazy(() =>
  import('./pages/TradeDetailPage').then((m) => ({ default: m.TradeDetailPage })),
)
const TransactionsPage = lazy(() =>
  import('./pages/TransactionsPage').then((m) => ({ default: m.TransactionsPage })),
)
const ChartsPage = lazy(() =>
  import('./pages/ChartsPage').then((m) => ({ default: m.ChartsPage })),
)
const PositionsPage = lazy(() =>
  import('./pages/PositionsPage').then((m) => ({ default: m.PositionsPage })),
)
const WatchlistPage = lazy(() =>
  import('./pages/WatchlistPage').then((m) => ({ default: m.WatchlistPage })),
)

function ConnectedShell() {
  useRecordPortfolioSnapshot()
  useMarketCalendar()
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/trades" element={<TradesPage />} />
        <Route path="/trades/:tradeId" element={<TradeDetailPage />} />
        <Route path="/transactions" element={<TransactionsPage />} />
        <Route path="/instruments/:symbol" element={<InstrumentDetails />} />
        <Route path="/charts" element={<ChartsPage />} />
        <Route path="/calendar" element={<PLCalendar />} />
        <Route path="/positions" element={<PositionsPage />} />
        <Route path="/watchlist" element={<WatchlistPage />} />
        <Route path="/accounts" element={<AccountsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>
    </Routes>
  )
}

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  oauth_no_code: 'The OAuth provider did not return an authorization code.',
  oauth_state_mismatch: 'OAuth state mismatch — please try connecting again.',
  oauth_exchange_failed:
    'Could not exchange the OAuth code for an access token. Check that your App Key, App Secret, and Callback URL in the Schwab developer portal match what you entered here.',
  vault_locked: 'Your credential vault is locked. Sign in again to finish connecting.',
  auth_required: 'Your session expired during OAuth. Sign in again to finish connecting.',
  no_accounts: 'No accounts were returned by the platform.',
  persist_failed: 'Could not save the new platform attachment. Try again.',
  unsupported_platform: 'Unsupported platform on OAuth callback.',
  unexpected: 'An unexpected error occurred during OAuth. Try again.',
}

interface OAuthQueryResult {
  attached?: string
  reauth?: boolean
  error?: string
}

function consumeOAuthQueryParams(): OAuthQueryResult {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  const attached = params.get('attached')
  const error = params.get('attach_error')
  const reauth = params.get('reauth') === '1'
  if (!attached && !error) return {}
  params.delete('attached')
  params.delete('attach_error')
  params.delete('platform')
  params.delete('reauth')
  const search = params.toString()
  const next = window.location.pathname + (search ? `?${search}` : '') + window.location.hash
  window.history.replaceState({}, '', next)
  if (error) return { error: OAUTH_ERROR_MESSAGES[error] ?? `OAuth error: ${error}` }
  if (attached) return { attached, reauth }
  return {}
}

export default function App() {
  const status = useAuthStore((s) => s.status)
  const accountId = useAuthStore((s) => s.accountId)
  const skippedPlatformAttach = useAuthStore((s) => s.skippedPlatformAttach)
  const setSession = useAuthStore((s) => s.setSession)
  const setConnection = useAuthStore((s) => s.setConnection)
  const setStatus = useAuthStore((s) => s.setStatus)
  const settingsHydrated = useSettingsStore((s) => s.hydrated)
  const hydrateSettings = useSettingsStore((s) => s.hydrate)
  const resetSettings = useSettingsStore((s) => s.reset)
  const [settingsError, setSettingsError] = useState<string | null>(null)

  useEffect(() => {
    // Read URL params synchronously on mount so we strip them before any
    // refresh/back-button can resurface them. We apply the resulting message
    // AFTER getSession resolves so the status transition doesn't wipe it.
    const oauth = consumeOAuthQueryParams()
    let cancelled = false
    getSession()
      .then((session) => {
        if (cancelled) return
        setSession({
          user: session.user,
          isAuthenticated: session.authenticated,
          vaultUnlocked: session.vaultUnlocked,
        })
        setConnection(session.connection)
        const nextStatus = !session.authenticated || !session.vaultUnlocked
          ? 'unauthenticated'
          : session.connection?.accountId || skippedPlatformAttach
            ? 'connected'
            : 'needs_platform'
        setStatus(nextStatus, oauth.error ?? null)
        if (oauth.attached && nextStatus === 'connected') {
          toast.success(
            oauth.reauth
              ? `Reauthorized ${oauth.attached}`
              : `Connected ${oauth.attached}`,
          )
        }
        if (oauth.error && nextStatus === 'connected') {
          // Already connected (re-attach attempt); UnlockScreen won't render,
          // so surface the error via toast instead of relying on errorMessage.
          toast.error(oauth.error, 'Connection failed')
        }
      })
      .catch((err) => {
        if (cancelled) return
        const message = err instanceof Error ? err.message : String(err)
        setStatus('error', oauth.error ?? message)
      })
    return () => {
      cancelled = true
    }
  }, [setConnection, setSession, setStatus, skippedPlatformAttach])

  useEffect(() => {
    if (status !== 'connected' || !accountId) return
    // Warm the SQLite cache; instrument search no longer needs a universe
    // sync — results are written through from Schwab searches on demand.
    initCache().catch((err) => {
      console.warn('[instruments] cache init failed — symbol search runs remote-only', err)
    })
  }, [status, accountId])

  // Hydrate per-user settings from the DB once auth is established. Held off
  // until `connected` so the GET carries the session cookie. On sign-out we
  // reset so the next login re-hydrates against the new user.
  useEffect(() => {
    if (status !== 'connected') {
      if (settingsHydrated) resetSettings()
      return
    }
    if (settingsHydrated) return
    let cancelled = false
    hydrateSettings()
      .then(() => {
        if (cancelled) return
        setSettingsError(null)
      })
      .catch((err) => {
        if (cancelled) return
        setSettingsError(err instanceof Error ? err.message : String(err))
      })
    return () => {
      cancelled = true
    }
  }, [status, settingsHydrated, hydrateSettings, resetSettings])

  if (status !== 'connected') {
    return <UnlockScreen />
  }

  if (!settingsHydrated) {
    return (
      <div className="flex h-dvh items-center justify-center bg-zinc-950 text-sm text-zinc-400">
        {settingsError ? `Settings error: ${settingsError}` : 'Loading settings…'}
      </div>
    )
  }

  return (
    <Routes>
      {/* Detached single-chart window. Sits above <ConnectedShell> so this
          window doesn't double-run the portfolio-snapshot recorder the main
          window already owns. */}
      <Route path="/chart-popout" element={<ChartPopoutPage />} />
      <Route path="*" element={<ConnectedShell />} />
    </Routes>
  )
}
